import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Payments as PaymentsIcon,
  Settings as SettingsIcon,
  ShoppingCart as ShoppingCartIcon,
  Web as WebIcon,
  ViewKanban as BoardIcon,
  RequestQuote as QuoteIcon,
  NotificationsNoneOutlined as NotificationsIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { COLORS, ON_COVER } from '../../theme';
import { Diamond, GridOverlay, Sparkle } from '../landing/LandingShapes';

const HEADER_META = {
  Dashboard: {
    tag: 'VISÃO GERAL',
    subtitle: 'Acompanhe os principais números, pedidos e resultados do estúdio em um só lugar.',
    icon: DashboardIcon,
  },
  'Cadastro de Clientes': {
    tag: 'CLIENTES',
    subtitle: 'Cadastre, edite e consulte clientes e informações de contato.',
    icon: PeopleIcon,
  },
  'Cadastro de Pedidos': {
    tag: 'PEDIDOS',
    subtitle: 'Crie novas produções e acompanhe status, valores e prazos.',
    icon: ShoppingCartIcon,
  },
  'Cadastro de Serviços': {
    tag: 'CATÁLOGO',
    subtitle: 'Mantenha os serviços e preços do catálogo sempre atualizados.',
    icon: AssignmentIcon,
  },
  Quadros: {
    tag: 'OPERAÇÃO',
    subtitle: 'O trabalho operacional da empresa, do pedido à retirada.',
    icon: BoardIcon,
  },
  Pagamentos: {
    tag: 'FINANCEIRO',
    subtitle: 'Gerencie faturas, status de pagamento e cobranças.',
    icon: PaymentsIcon,
  },
  'Gerenciar Orçamento': {
    tag: 'SITE',
    subtitle: 'Organize os serviços exibidos na página pública de orçamento.',
    icon: QuoteIcon,
  },
  'Landing Page': {
    tag: 'SITE',
    subtitle: 'Edite os textos, imagens e seções da página inicial.',
    icon: WebIcon,
  },
  Boards: {
    tag: 'ORGANIZAÇÃO',
    subtitle: 'Organize tarefas e produções em quadros visuais.',
    icon: BoardIcon,
  },
  'Meus Quadros': {
    tag: 'ORGANIZAÇÃO',
    subtitle: 'Organize tarefas e produções em quadros visuais.',
    icon: BoardIcon,
  },
  'Configurar Landing Page': {
    tag: 'SITE',
    subtitle: 'Edite os textos, imagens e seções da página inicial.',
    icon: WebIcon,
  },
  'Gerenciar Serviços': {
    tag: 'SITE',
    subtitle: 'Organize os serviços exibidos na página pública de orçamento.',
    icon: QuoteIcon,
  },
  'Cadastros Pendentes': {
    tag: 'GESTÃO',
    subtitle: 'Analise e aprove os cadastros enviados pelo site.',
    icon: PeopleIcon,
  },
  'Manutenção de Storage': {
    tag: 'SISTEMA',
    subtitle: 'Limpe e organize os arquivos armazenados com segurança.',
    icon: SettingsIcon,
  },
  'Conversor de Texto': {
    tag: 'FERRAMENTAS',
    subtitle: 'Converta e formate textos para usar no catálogo e nos anúncios.',
    icon: WebIcon,
  },
  Indicações: {
    tag: 'CRESCIMENTO',
    subtitle: 'Acompanhe indicadores, indicados e comissões.',
    icon: PeopleIcon,
  },
  'Instagram IA': {
    tag: 'MARKETING',
    subtitle: 'Gere legendas, hashtags e ordenação de fotos com inteligência artificial.',
    icon: WebIcon,
  },
  Configurações: {
    tag: 'SISTEMA',
    subtitle: 'Defina preferências do painel, como a pasta dos pedidos no seu computador.',
    icon: SettingsIcon,
  },
  Notificações: {
    tag: 'COMUNICAÇÃO',
    subtitle: 'Acompanhe pedidos, pagamentos, arquivos e avisos do sistema em um só lugar.',
    icon: NotificationsIcon,
  },
  Segurança: {
    tag: 'SISTEMA',
    subtitle: 'Verificação em duas etapas (TOTP) e acesso protegido para a conta administrativa.',
    icon: SecurityIcon,
  },
  'Editar Pedido': {
    tag: 'PEDIDOS',
    subtitle: 'Atualize os dados da produção e os valores do pedido.',
    icon: ShoppingCartIcon,
  },
  'Editar Cliente': {
    tag: 'CLIENTES',
    subtitle: 'Atualize os dados do cliente.',
    icon: PeopleIcon,
  },
  'Dados do Cliente': {
    tag: 'CLIENTES',
    subtitle: 'Consulte o histórico e as informações do cliente.',
    icon: PeopleIcon,
  },
  'Editar Cadastro Pendente': {
    tag: 'GESTÃO',
    subtitle: 'Revise os dados enviados antes de aprovar o cadastro.',
    icon: PeopleIcon,
  },
};

/**
 * Capa padrão das páginas do admin.
 *
 * Um único hero para todas as telas: grafite + grade da marca, rubrica com
 * diamante, ícone em caixa laranja e título Poppins 900. O dashboard chegou a
 * ter uma capa própria (mais baixa, sem ícone), e era a única tela fora da
 * família — hoje ele usa este mesmo componente.
 *
 * Contrato responsivo (mesma identidade em todas as páginas):
 * - **desktop (`md`+)**: rubrica, ícone, título, subtítulo e `meta`. Sem CTA —
 *   a ação primária da página vive na toolbar/conteúdo, onde o mouse está.
 * - **mobile (`< md`)**: o mesmo bloco empilhado + o CTA laranja em pílula com
 *   largura total, logo abaixo do subtítulo.
 *
 * Por isso `actionButton` é o CTA **mobile**. Quem precisar da mesma ação no
 * desktop deve renderizá-la na barra de ferramentas da página (ex.:
 * `PedidosToolbar`, `DashboardToolbar`) para não duplicar rótulo na tela.
 *
 * `meta` é a linha de apoio opcional (ex.: período + hora da última leitura no
 * dashboard) e `actionButton.disabled` cobre ações que esperam uma requisição.
 */
const PageHeader = ({ title, subtitle, tag, icon, meta = null, actionButton = null, children }) => {
  const headerMeta = HEADER_META[title] || {};
  const IconComponent = React.isValidElement(icon)
    ? icon.type
    : icon || headerMeta.icon || SettingsIcon;
  const Tag = tag || headerMeta.tag || 'PAINEL ADMINISTRATIVO';
  const Subtitle =
    subtitle || headerMeta.subtitle || 'Gerencie esta área do estúdio com praticidade.';
  const iconSx = { fontSize: { xs: 24, md: 28 } };

  return (
    <Box>
      <Box
        data-testid="page-hero"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          bgcolor: COLORS.dark,
          color: COLORS.cream,
          borderRadius: { xs: '20px', md: '26px' },
          p: { xs: 3, md: 4.5 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Box
          data-testid="page-hero-grid"
          aria-hidden="true"
          sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        >
          <GridOverlay dark />
        </Box>
        <Sparkle
          size={44}
          color={COLORS.orange}
          sx={{
            position: 'absolute',
            top: 18,
            right: '5%',
            opacity: 0.9,
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <Diamond
          size={14}
          color={COLORS.orangeSoft}
          sx={{
            position: 'absolute',
            bottom: 24,
            right: '12%',
            opacity: 0.7,
            display: { xs: 'none', md: 'block' },
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'flex-end' },
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
              <Diamond size={11} color={COLORS.orange} />
              <Typography
                sx={{
                  color: COLORS.orange,
                  fontWeight: 800,
                  letterSpacing: 1.6,
                  fontSize: { xs: '0.68rem', md: '0.76rem' },
                  textTransform: 'uppercase',
                }}
              >
                {Tag}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: { xs: 44, md: 52 },
                  height: { xs: 44, md: 52 },
                  borderRadius: '14px',
                  bgcolor: 'rgba(235,91,37,0.16)',
                  border: '1px solid rgba(235,91,37,0.45)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: COLORS.orange,
                  flexShrink: 0,
                }}
              >
                {React.isValidElement(icon) ? (
                  React.cloneElement(icon, { sx: iconSx })
                ) : (
                  <IconComponent sx={iconSx} />
                )}
              </Box>
              <Typography
                component="h1"
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: '1.7rem', sm: '2.1rem', md: '2.6rem' },
                  lineHeight: 1.08,
                  letterSpacing: '-0.02em',
                }}
              >
                {title}
              </Typography>
            </Box>

            {Subtitle && (
              <Typography
                sx={{
                  mt: 1.6,
                  color: ON_COVER.soft,
                  fontSize: { xs: '0.92rem', md: '1.02rem' },
                  lineHeight: 1.65,
                  maxWidth: 640,
                }}
              >
                {Subtitle}
              </Typography>
            )}

            {meta && (
              <Typography
                component="div"
                sx={{
                  mt: 1.2,
                  color: ON_COVER.soft,
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  lineHeight: 1.5,
                  // Números do período/hora alinham em coluna (tabular).
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {meta}
              </Typography>
            )}

            {/* CTA mobile: pílula laranja de largura total logo abaixo do
                subtítulo. No desktop a ação equivalente mora na toolbar. */}
            {actionButton && (
              <Box
                data-testid="page-hero-action"
                sx={{ display: { xs: 'flex', md: 'none' }, mt: 2.5, width: '100%' }}
              >
                <Button
                  variant="contained"
                  startIcon={actionButton.icon || <AddIcon />}
                  onClick={actionButton.onClick}
                  disabled={Boolean(actionButton.disabled)}
                  sx={{
                    bgcolor: actionButton.color === 'error' ? COLORS.errorDark : COLORS.orange,
                    color: COLORS.white,
                    borderRadius: '50px',
                    px: 2.6,
                    py: 1.3,
                    width: '100%',
                    justifyContent: 'center',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor:
                        actionButton.color === 'error' ? COLORS.errorDeep : COLORS.orangeDeep,
                    },
                    '&.Mui-disabled': { bgcolor: ON_COVER.line, color: ON_COVER.muted },
                    ...(actionButton.sx || {}),
                  }}
                >
                  {actionButton.label}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {children}
    </Box>
  );
};

export default PageHeader;
